import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { CLIENT_ID_PREFIX } from '@constants/client-id';
import { STRESS_INDEX } from '@constants/fhir';

import { ObservationRecord } from '@models/observation';

import { roundPrecision } from './conversion';

dayjs.extend(timezone);
dayjs.extend(utc);

const DATE_FORMATS: Record<string, string> = {
  date: 'MMM D, YYYY',
  date_short: 'MMM DD/YY',
  datetime: 'MMM D, YYYY h:mm A',
  time: 'h:mm A',
  timestamp: 'MM/DD/YYYY hh:mm:ss',
  datetime_short: 'MMM D, h:mm A',
};

/**
 * Format a date object as a string
 * @param param0 Date to format and formatter type
 * @returns A formatted date string
 */
export function formatDate({
  date,
  formatter = 'date',
  fallback = '',
  utc = false,
  tz = null,
}: {
  /**
   * Date to format
   */
  date: Date | number | string | null | undefined;
  /**
   * Should the date be shown in UTC time?
   *
   * @description Use to display non-relative dates, like birthdays
   */
  utc?: boolean;
  /**
   * Formatting string to use
   *
   * Defaults to `date`
   * Can be one of 'date', 'date_short', 'datetime', 'time', 'timestamp' or a custom format
   *
   */
  formatter?: string;
  /**
   * String to use when an invalid date is provided
   */
  fallback?: string;
  /**
   * Timezone
   */
  tz?: string | null;
}): string {
  if (!date) {
    return fallback;
  }
  try {
    let day = dayjs(date);
    if (!day.isValid() || day.year() <= 0) {
      return fallback;
    }
    if (utc) {
      day = day.utc();
    } else if (tz) {
      day = day.tz(tz);
    }
    return day.format(
      formatter in DATE_FORMATS ? DATE_FORMATS[formatter] : formatter
    );
  } catch {
    return fallback;
  }
}

export function toISO({
  date,
  to,
  of,
  utc = false,
}: {
  date: Date | dayjs.Dayjs | number | string | null | undefined;
  to: 'end' | 'start';
  of: 'day';
  utc?: boolean;
}) {
  if (to === 'start' && date) {
    return dayjs(date)
      .startOf(of)
      .add(utc ? dayjs(date).utcOffset() : 0, 'minutes')
      .toISOString();
  } else if (to === 'end' && date) {
    return dayjs(date)
      .endOf(of)
      .add(utc ? dayjs(date).utcOffset() : 0, 'minutes')
      .toISOString();
  }
  return undefined;
}

export function formatAddress(
  line?: (string | null)[] | null,
  city?: string | null,
  postalCode?: string | null,
  province?: string | null,
  country?: string | null
) {
  const address = `${line ? `${line.join(', ')} ` : ''}${
    city ? `${city}, ` : ''
  }${province ? `${province} ` : ''}${postalCode ? `${postalCode}, ` : ''}${
    country ? `${country} ` : ''
  }`;
  return address.replace(/(?<whitespace>,\s|\s)$/u, '');
}

export function formatName(...args: (string | null | undefined)[]) {
  return args.reduce(
    (acc, curr, index) =>
      acc +
      (curr?.trim()
        ? `${curr.trim()}${index === args.length - 1 ? '' : ' '}`
        : ''),
    ''
  );
}

export function formatVitalSign(
  sign:
    | number[]
    | ObservationRecord
    | {
        value: number[] | string[] | number | string | null;
        unit: string | null | undefined;
      },
  includeUnits = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  format = (value: number | string, _unit?: string | null) =>
    String(typeof value === 'number' ? roundPrecision(value, 0) : value),
  multiplier = 1
) {
  if (sign instanceof ObservationRecord && sign.code === STRESS_INDEX) {
    const value = sign.interpretation;
    if (value) {
      return value;
    }
  }
  if (sign instanceof Array) {
    if (!(sign instanceof Array) || sign.length === 0) return '- -';
    return sign
      .sort((a, b) => (a > b ? -1 : 1))
      .reduce<string>((acc, curr, index) => {
        if (index === 0) {
          return `${format(curr) || '- -'}`;
        } else if (curr) {
          return (acc += `/${format(curr) || '- -'}`);
        }
        return acc;
      }, '');
  }
  if (sign.value instanceof Array) {
    return `${sign.value
      .sort((a, b) => (a > b ? -1 : 1))
      .map((v) => format(v))
      .join('/')} ${includeUnits ? (sign.unit ?? '') : ''}`;
  }

  const v =
    typeof sign.value === 'number' && sign instanceof ObservationRecord
      ? sign.value * multiplier
      : sign.value;

  return typeof v === 'boolean'
    ? ''
    : `${format(v ?? '')} ${includeUnits ? (sign.unit ?? '') : ''}`;
}

export function formatTime(timeNum?: Date | number | null) {
  if (timeNum instanceof Date) {
    return dayjs(timeNum).format('h:mm a');
  }
  if (typeof timeNum !== 'number') return '';
  if (timeNum < 0 || timeNum >= 2400) return 'Out of Range';

  const checkedTime =
    timeNum >= 1300 && timeNum < 2400 ? timeNum - 1200 : timeNum;
  const amPm = timeNum < 1200 ? 'am' : 'pm';

  let parsedTime = String(checkedTime).padStart(2, '0');
  if (checkedTime < 100) parsedTime = `12${parsedTime}`;

  return `${parsedTime.slice(0, -2)}:${parsedTime.slice(-2)} ${amPm}`;
}

export function formatDuration(duration?: number | string) {
  if (duration === undefined) return '';
  const durationNum =
    typeof duration === 'number' ? duration : parseFloat(duration);
  if (isNaN(durationNum)) return '';
  const hours = Math.floor(durationNum);
  const minutes = Math.round((durationNum - Math.floor(durationNum)) * 60);

  const hoursStr = String(hours).padStart(1, '0');
  const minutesStr = String(minutes).padStart(2, '0');

  return `${hoursStr}:${minutesStr}`;
}

export function formatPhone(phoneNumber?: string | null) {
  if (typeof phoneNumber !== 'string' || !phoneNumber) return '';

  const stripePhone = phoneNumber.replace(/[()-+\s]/gu, '');
  const stripePhoneNum = parseInt(stripePhone, 10);

  if (!isNaN(stripePhoneNum)) {
    if (stripePhone.length === 10) {
      return `(${stripePhone.substring(0, 3)}) ${stripePhone.substring(
        3,
        6
      )}-${stripePhone.substring(6, 10)}`;
    } else if (stripePhone.length === 11) {
      return `+${stripePhone.substring(0, 1)} (${stripePhone.substring(
        1,
        4
      )}) ${stripePhone.substring(4, 7)}-${stripePhone.substring(7, 11)}`;
    }
  }
  return phoneNumber;
}

export function formatFacility(
  name?: string | null,
  city?: string | null,
  province?: string | null,
  country?: string | null
) {
  const location = formatAddress(null, city, null, province, country);
  if (name && location) return `${name} (${location})`;
  if (name) return `${name}`;
  if (location) return `${location}`;
  return '';
}

export function formatDateTimeLabel(date?: Date, label?: string, alt?: string) {
  if (date) {
    let formatter: keyof typeof DATE_FORMATS = 'date';
    // Midnight case
    if (date.getTime() !== dayjs(date).startOf('day').toDate().getTime()) {
      formatter = 'datetime';
    }
    return `${label}: ${formatDate({ date, formatter })}`;
  }
  if (alt) return `${label}: ${alt}`;
  return '';
}

export function formatOrdinal(number: number) {
  switch (number) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export function formatBirthday(birthDate?: string | null) {
  if (!birthDate) return '-';
  return formatDate({ date: birthDate });
}

export function formatEmergencyContact(
  name: string,
  relationship?: string,
  phone?: string | null
) {
  return `${name ? `${name} -` : ''} ${phone ?? ''} ${
    relationship ? `(${relationship})` : ''
  }`;
}

/**
 * Format a related person's name and phone number
 * @param name Name of related person
 * @param phone Phone number of related person
 */
export function formatRelatedPerson(
  name?: string | null,
  phone?: string | null
) {
  return `${name ?? '-'} ${phone ? `- ${phone}` : ''}`;
}
/**
 * Capitalize the first letter of a string
 * @param str String to capitalize
 */
export function capitalize<T extends string>(
  str: T | undefined
): Capitalize<T> {
  if (!str) {
    return '' as Capitalize<T>;
  }
  switch (str.length) {
    case 0:
      return str as Capitalize<T>;
    case 1:
      return str.toUpperCase() as Capitalize<T>;
    default:
      return str
        .charAt(0)
        .toUpperCase()
        .concat(str.slice(1).toLowerCase()) as Capitalize<T>;
  }
}

/**
 * Capitalize the first letter of each word in a string
 * @param str String to capitalize
 * @param retainAllCaps true if all uppercase string should be retained as-is
 */
export function capitalizeAll(str: string, retainAllCaps?: boolean) {
  retainAllCaps = retainAllCaps ?? false;
  const strList = str.split(' ');
  for (let i = 0; i < strList.length; i += 1) {
    // Preserve all-uppercase words if flag is true
    if (
      retainAllCaps &&
      strList[i].length > 0 &&
      strList[i] === strList[i].toUpperCase()
    ) {
      continue;
    }
    strList[i] = capitalize(strList[i]);
  }
  return strList.join(' ');
}

export function splitRecordsByDate<T>(
  data: T[],
  dateKey: keyof T,
  date: Date = new Date()
): [T[], T[]] {
  const past = data.filter((i) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const entry =
      typeof i[dateKey] === 'function'
        ? (i[dateKey] as () => unknown)()
        : i[dateKey];
    if (
      entry instanceof Date ||
      typeof entry === 'string' ||
      typeof entry === 'number'
    ) {
      const recordDate = dayjs(entry);
      return recordDate.isBefore(date);
    }
    return true;
  });
  const upcoming = data.filter((i) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const entry =
      typeof i[dateKey] === 'function'
        ? (i[dateKey] as () => unknown)()
        : i[dateKey];
    if (
      entry instanceof Date ||
      typeof entry === 'string' ||
      typeof entry === 'number'
    ) {
      const recordDate = dayjs(entry);
      return recordDate.isAfter(date) || recordDate.isSame(date);
    }
    return false;
  });
  return [past, upcoming];
}

/**
 * Pad client ID so it is 6 numbers
 * @param id Client id
 */
export function formatClientId(id: number | string, include_prefix: boolean) {
  return `${include_prefix ? CLIENT_ID_PREFIX : ''}${id
    .toString()
    .padStart(6, '0')}`;
}

/**
 * Pad patient ID so it starts with `Patient/`
 * @param patient_id Patient ID
 */
export function formatPatientId(patient_id?: string | null): string | null {
  if (!patient_id) {
    return null;
  }
  const padStr = 'Patient/';
  return patient_id.startsWith(padStr) ? patient_id : `${padStr}${patient_id}`;
}

export function formatMoney(
  amount = 0,
  decimalPlaces = 2,
  language = 'EN',
  locale = 'ca',
  currency = 'CAD'
) {
  return new Intl.NumberFormat(`${language}-${locale}`, {
    maximumFractionDigits: decimalPlaces,
    minimumFractionDigits: decimalPlaces,
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format a filename
 * @param filename Name of the file
 * @returns Formatted filename
 */
export function formatFilename(filename: string | null | undefined): string {
  try {
    return decodeURIComponent(
      (filename ?? '').replace(/%(?![0-9][0-9a-fA-F]+)/gu, '%25')
    );
  } catch {
    try {
      return decodeURIComponent(filename ?? '');
    } catch {
      /* empty */
    }
    return filename ?? '';
  }
}

/**
 * Format a name into it's initials
 * @param firstName First name
 * @param lastName Last name
 * @returns The initials of the first and last name
 */
export function formatInitials(firstName?: string, lastName?: string) {
  let firstLetter = '';
  let secondLetter = '';
  if (firstName) {
    [firstLetter] = firstName.toUpperCase();
  }
  if (lastName) {
    [secondLetter] = lastName.toUpperCase();
  }
  if (!(firstLetter || secondLetter)) {
    return null;
  }
  return (
    formatName(firstLetter, undefined, secondLetter)?.replace(/\s/gu, '') ?? ''
  );
}

/**
 * Format a number
 * @param number Number to format
 * @returns A formatted number string
 */
export function formatNumber(
  num?: number,
  minDigits = 0,
  maxDigits = 0
): string {
  if (typeof num !== 'number') {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: maxDigits,
    minimumFractionDigits: minDigits,
  }).format(num);
}

/**
 * Transforms camel-cased FHIR code to a capitalized, friendly format
 * @param code FHIR code
 * @returns blood-pressure --> Blood Pressure
 */
export function formatFhirCode(code: string) {
  const parts = code.split('-');
  return parts.map((part) => capitalize(part)).join(' ');
}
